import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { BodyText, IconName, theme } from '@sg/garnish';

import { StripePaymentMethodImage } from '@order/integrations-stripe';

import { usePaymentBreakdown } from '../../../../hooks';

export const StripePaymentMethod = (props: StripePaymentMethodProps) => {
  const {
    isDisabled,
    hasNoPaymentMethod,
    stripePaymentMethod,
    stripePaymentMethodIcon,
    stripePaymentMethodImage,
    onPressStripePaymentMethod,
    paymentBreakdown,
  } = props;

  const { formattedTotalStripe } = paymentBreakdown;
  const { formatMessage } = useIntl();

  const stripePaymentMethodLabel = hasNoPaymentMethod
    ? stripePaymentMethod
    : formatMessage(messages.paymentMethod, { card: stripePaymentMethod });

  return (
    <TouchableOpacity
      style={styles.paymentMethod}
      accessibilityRole="button"
      accessibilityLabel={formatMessage(messages.changePaymentMethod)}
      disabled={isDisabled}
      onPress={onPressStripePaymentMethod}
    >
      <View style={[styles.info, styles.infoText]}>
        <StripePaymentMethodImage
          imageBase64={stripePaymentMethodImage}
          icon={stripePaymentMethodIcon}
        />

        <BodyText style={styles.paymentMethodLink} sizeMatch={['14']}>
          {stripePaymentMethodLabel}
        </BodyText>
      </View>

      <BodyText sizeMatch={['18']} bold>
        {formattedTotalStripe}
      </BodyText>
    </TouchableOpacity>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  changePaymentMethod: {
    defaultMessage: 'Change payment method',
    description: 'Bag | Payment Methods | Change payment method',
  },
  paymentMethod: {
    defaultMessage: 'Paying with {card}',
    description: 'Bag | Payment Methods | Payment method',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  paymentMethod: {
    gap: theme.spacing['2'],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing['4'],
    paddingVertical: theme.spacing['3'],
  },
  info: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['2'],
  },
  infoText: {
    flex: 1,
    flexWrap: 'wrap',
  },
  paymentMethodLink: {
    color: theme.colors.SPINACH,
    textDecorationLine: 'underline',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type StripePaymentMethodProps = {
  isDisabled: boolean;
  hasNoPaymentMethod: boolean;
  stripePaymentMethod: string | undefined;
  stripePaymentMethodIcon: IconName | undefined;
  stripePaymentMethodImage: string | undefined;
  onPressStripePaymentMethod: (() => void) | undefined;
  paymentBreakdown: ReturnType<typeof usePaymentBreakdown>;
};
